import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Stage, Layer, Line, Arrow, Text, Image as KonvaImage } from 'react-konva';
import axios from 'axios';
import { documentAPI } from '../../services/api';
import './photoAnnotator.css';

const COLORS = [
    { name: 'Red', value: '#ef4444' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Yellow', value: '#eab308' },
    { name: 'White', value: '#ffffff' },
    { name: 'Black', value: '#000000' },
];

const PhotoAnnotator = ({ photo, imageUrl, projectId, onClose, onSaved }) => {
    const stageRef = useRef(null);
    const [tool, setTool] = useState('freehand');
    const [color, setColor] = useState('#ef4444');
    const [annotations, setAnnotations] = useState([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [image, setImage] = useState(null);
    const [imageReady, setImageReady] = useState(false);
    const [stageDimensions, setStageDimensions] = useState({ width: 0, height: 0 });
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0, x: 0, y: 0 });
    const [saving, setSaving] = useState(false);
    const [pendingText, setPendingText] = useState(null); // { stageX, stageY } — where the text will be placed
    const [textValue, setTextValue] = useState('');
    const textInputRef = useRef(null);
    const blobUrlRef = useRef(null);
    const toolbarHeight = 56;

    // Load the source image as a blob URL to avoid CORS/tainted-canvas issues.
    // We fetch a FRESH presigned URL from the API instead of reusing imageUrl,
    // because the browser may have cached the img-tag response without CORS headers,
    // which causes fetch() on the same URL to fail.
    useEffect(() => {
        if (!photo?.id) return;
        let cancelled = false;

        (async () => {
            try {
                const { data } = await documentAPI.getDownloadUrl(photo.id);
                if (cancelled) return;

                const response = await fetch(data.downloadUrl);
                if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
                const blob = await response.blob();
                if (cancelled) return;

                const objectUrl = URL.createObjectURL(blob);
                blobUrlRef.current = objectUrl;

                const img = new window.Image();
                img.onload = () => {
                    if (!cancelled) {
                        setImage(img);
                        setImageReady(true);
                    }
                };
                img.onerror = () => {
                    console.error('Failed to load image into canvas');
                };
                img.src = objectUrl;
            } catch (err) {
                console.error('Failed to fetch image for annotation:', err);
            }
        })();

        return () => {
            cancelled = true;
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [photo?.id]);

    // Calculate stage and image dimensions
    const updateDimensions = useCallback(() => {
        const width = window.innerWidth;
        const height = window.innerHeight - toolbarHeight;
        setStageDimensions({ width, height });

        if (image) {
            const scale = Math.min(width / image.width, height / image.height, 1);
            const imgW = image.width * scale;
            const imgH = image.height * scale;
            setImageDimensions({
                width: imgW,
                height: imgH,
                x: (width - imgW) / 2,
                y: (height - imgH) / 2,
            });
        }
    }, [image, toolbarHeight]);

    useEffect(() => {
        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [updateDimensions]);

    // Lock body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    // Capture Escape key — dismiss text input if active, otherwise close annotator.
    // Always stopImmediatePropagation to prevent lightbox from closing underneath.
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') {
                e.stopImmediatePropagation();
                if (pendingText) {
                    setPendingText(null);
                    setTextValue('');
                } else {
                    onClose();
                }
            }
        };
        window.addEventListener('keydown', handleKey, true);
        return () => window.removeEventListener('keydown', handleKey, true);
    }, [onClose, pendingText]);

    // Auto-focus the text input when it appears
    useEffect(() => {
        if (pendingText && textInputRef.current) {
            textInputRef.current.focus();
        }
    }, [pendingText]);

    const handleMouseDown = (e) => {
        if (pendingText) return; // ignore clicks while text input is open
        const stage = e.target.getStage();
        const pos = stage.getPointerPosition();

        if (tool === 'freehand') {
            setIsDrawing(true);
            setAnnotations(prev => [...prev, {
                type: 'freehand',
                points: [pos.x, pos.y],
                color,
            }]);
        } else if (tool === 'arrow') {
            setIsDrawing(true);
            setAnnotations(prev => [...prev, {
                type: 'arrow',
                points: [pos.x, pos.y, pos.x, pos.y],
                color,
            }]);
        } else if (tool === 'text') {
            setPendingText({ stageX: pos.x, stageY: pos.y });
            setTextValue('');
        }
    };

    const handleMouseMove = (e) => {
        if (!isDrawing) return;
        const stage = e.target.getStage();
        const pos = stage.getPointerPosition();

        setAnnotations(prev => {
            const updated = [...prev];
            const last = { ...updated[updated.length - 1] };

            if (tool === 'freehand') {
                last.points = [...last.points, pos.x, pos.y];
            } else if (tool === 'arrow') {
                last.points = [last.points[0], last.points[1], pos.x, pos.y];
            }

            updated[updated.length - 1] = last;
            return updated;
        });
    };

    const handleMouseUp = () => {
        setIsDrawing(false);
    };

    const handleTextSubmit = () => {
        if (textValue.trim() && pendingText) {
            setAnnotations(prev => [...prev, {
                type: 'text',
                x: pendingText.stageX,
                y: pendingText.stageY,
                text: textValue.trim(),
                color,
            }]);
        }
        setPendingText(null);
        setTextValue('');
    };

    const handleTextCancel = () => {
        setPendingText(null);
        setTextValue('');
    };

    const handleUndo = () => {
        setAnnotations(prev => prev.slice(0, -1));
    };

    const handleClear = () => {
        setAnnotations([]);
    };

    const handleSave = async () => {
        if (!stageRef.current || saving) return;
        setSaving(true);

        try {
            const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });

            // Convert data URL to blob
            const res = await fetch(dataUrl);
            const blob = await res.blob();

            const originalName = photo.fileName.replace(/\.[^/.]+$/, '');
            const fileName = `annotated-${originalName}.png`;

            const { data: urlData } = await documentAPI.getUploadUrl({
                projectId,
                fileName,
                contentType: 'image/png',
                documentType: 0,
            });

            await axios.put(urlData.uploadUrl, blob, {
                headers: { 'Content-Type': 'image/png' },
            });

            await documentAPI.saveMetadata({
                projectId,
                fileName,
                fileKey: urlData.fileKey,
                fileSize: blob.size,
                contentType: 'image/png',
                documentType: 0,
            });

            if (onSaved) onSaved();
        } catch (err) {
            console.error('Failed to save annotated image:', err);
            alert('Failed to save annotated image. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="annotator-overlay" onClick={(e) => e.stopPropagation()}>
            <div className="annotator-toolbar">
                <div className="annotator-tools">
                    <button
                        className={`annotator-tool-btn${tool === 'freehand' ? ' active' : ''}`}
                        onClick={() => setTool('freehand')}
                    >
                        <i className="fas fa-pen"></i> <span>Draw</span>
                    </button>
                    <button
                        className={`annotator-tool-btn${tool === 'arrow' ? ' active' : ''}`}
                        onClick={() => setTool('arrow')}
                    >
                        <i className="fas fa-long-arrow-alt-right"></i> <span>Arrow</span>
                    </button>
                    <button
                        className={`annotator-tool-btn${tool === 'text' ? ' active' : ''}`}
                        onClick={() => setTool('text')}
                    >
                        <i className="fas fa-font"></i> <span>Text</span>
                    </button>

                    <div className="annotator-divider" />

                    <div className="annotator-colors">
                        {COLORS.map(c => (
                            <button
                                key={c.value}
                                className={`annotator-color-swatch${color === c.value ? ' active' : ''}`}
                                style={{ backgroundColor: c.value }}
                                onClick={() => setColor(c.value)}
                                title={c.name}
                            />
                        ))}
                    </div>

                    <div className="annotator-divider" />

                    <button
                        className="annotator-tool-btn"
                        onClick={handleUndo}
                        disabled={annotations.length === 0}
                    >
                        <i className="fas fa-undo"></i> <span>Undo</span>
                    </button>
                    <button
                        className="annotator-tool-btn"
                        onClick={handleClear}
                        disabled={annotations.length === 0}
                    >
                        <i className="fas fa-eraser"></i> <span>Clear</span>
                    </button>
                </div>

                <div className="annotator-actions">
                    <button className="annotator-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="annotator-btn-save"
                        onClick={handleSave}
                        disabled={saving || annotations.length === 0}
                    >
                        <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {pendingText && (
                <div className="annotator-text-bar">
                    <span className="annotator-text-bar-label">
                        <i className="fas fa-font"></i> Enter label:
                    </span>
                    <input
                        ref={textInputRef}
                        className="annotator-text-bar-input"
                        type="text"
                        value={textValue}
                        onChange={(e) => setTextValue(e.target.value)}
                        placeholder="Type text here..."
                        style={{ color }}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleTextSubmit();
                            if (e.key === 'Escape') handleTextCancel();
                        }}
                    />
                    <button
                        className="annotator-text-bar-btn add"
                        onClick={handleTextSubmit}
                        disabled={!textValue.trim()}
                    >
                        Add
                    </button>
                    <button className="annotator-text-bar-btn" onClick={handleTextCancel}>
                        Cancel
                    </button>
                </div>
            )}

            <div className="annotator-canvas">
                {!imageReady && (
                    <div className="annotator-loading">
                        <i className="fas fa-spinner fa-spin"></i> Loading image...
                    </div>
                )}
                <Stage
                    ref={stageRef}
                    width={stageDimensions.width}
                    height={stageDimensions.height}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onTouchStart={handleMouseDown}
                    onTouchMove={handleMouseMove}
                    onTouchEnd={handleMouseUp}
                    style={{ cursor: tool === 'text' ? 'text' : 'crosshair' }}
                >
                    <Layer>
                        {image && (
                            <KonvaImage
                                image={image}
                                x={imageDimensions.x}
                                y={imageDimensions.y}
                                width={imageDimensions.width}
                                height={imageDimensions.height}
                            />
                        )}

                        {annotations.map((ann, i) => {
                            if (ann.type === 'freehand') {
                                return (
                                    <Line
                                        key={i}
                                        points={ann.points}
                                        stroke={ann.color}
                                        strokeWidth={3}
                                        lineCap="round"
                                        lineJoin="round"
                                        tension={0.5}
                                        globalCompositeOperation="source-over"
                                    />
                                );
                            }
                            if (ann.type === 'arrow') {
                                return (
                                    <Arrow
                                        key={i}
                                        points={ann.points}
                                        stroke={ann.color}
                                        fill={ann.color}
                                        strokeWidth={3}
                                        pointerLength={12}
                                        pointerWidth={10}
                                    />
                                );
                            }
                            if (ann.type === 'text') {
                                return (
                                    <Text
                                        key={i}
                                        x={ann.x}
                                        y={ann.y}
                                        text={ann.text}
                                        fontSize={20}
                                        fill={ann.color}
                                        fontFamily="Arial"
                                        fontStyle="bold"
                                        shadowColor="rgba(0,0,0,0.5)"
                                        shadowBlur={4}
                                        shadowOffsetX={1}
                                        shadowOffsetY={1}
                                    />
                                );
                            }
                            return null;
                        })}
                    </Layer>
                </Stage>

            </div>
        </div>
    );
};

export default PhotoAnnotator;
